/**
 * Board3DCatalog - Registry and Schema Validation for 3D Cribbage Boards
 * Manages selectable 3D boards, GLB asset references, hole coordinate mappings,
 * and camera/lighting presets.
 * 
 * Board camera angle configuration via cameraDefaults:
 * - alpha: Horizontal orbit angle (yaw) in radians (default: -Math.PI / 2, looking down Z-axis).
 * - alphaDeg: Convenient horizontal angle in degrees (e.g., -90 for front, 0 for right, 90 for back, 180 for left).
 * - beta: Vertical elevation/pitch angle in radians (default: Math.PI / 5.2 ≈ 0.604 rad, where 0 is directly overhead).
 * - betaDeg: Convenient vertical pitch angle in degrees (e.g., 35, 45, 60).
 */

import defaultBoardsData from '../../boards/default-boards.json' with { type: 'json' };

export let DEFAULT_3D_BOARDS = Array.isArray(defaultBoardsData)
  ? defaultBoardsData.map(b => Object.assign({}, b))
  : [];

export class Board3DCatalog {
  static CUSTOM_STORAGE_KEY = 'cc_custom_3d_boards';
  static DEFAULT_JSON_PATH = 'boards/default-boards.json';
  static _fallbackBoards = DEFAULT_3D_BOARDS.map(b => Object.assign({}, b));
  static _boards = new Map(DEFAULT_3D_BOARDS.map(b => [b.id, Object.assign({}, b)]));
  static _customBoards = new Map();
  static _customLoaded = false;
  static _jsonLoaded = false;
  static _jsonLoadPromise = null;
  static _jsonSource = null;

  /**
   * Returns whether default boards have been loaded from an external JSON source.
   * @returns {boolean}
   */
  static get jsonLoaded() {
    return this._jsonLoaded;
  }

  /**
   * Returns the source URL/path of the loaded JSON boards, or null if using built-in defaults.
   * @returns {string|null}
   */
  static get jsonSource() {
    return this._jsonSource;
  }

  /**
   * Returns the list of currently registered default 3D boards.
   * @returns {Array<Object>}
   */
  static get defaultBoards() {
    return Array.from(this._boards.values());
  }

  /**
   * Loads default 3D boards from an external JSON file, URL, raw JSON string, or object.
   * Updates Board3DCatalog._boards and DEFAULT_3D_BOARDS without requiring bundle recompilation.
   *
   * @param {string|Array|Object|File|Blob} [source=null] - JSON URL, file path, board array, object, or File instance.
   * @param {Object} [options={}]
   * @param {boolean} [options.replace=true] - If true, replaces default boards; if false, merges by ID.
   * @param {boolean} [options.syncDropdown=true] - If true, syncs the DOM #opt3DBoard dropdown after load.
   * @param {boolean} [options.notify=true] - If true, dispatches 'board3d:boards-loaded' event on document.
   * @returns {Promise<{ success: boolean, count: number, boards: Array<Object>, source: string|null, error?: string }>}
   */
  static async loadDefaultBoards(source = null, options = {}) {
    const replace = options.replace !== false;
    const syncDropdown = options.syncDropdown !== false;
    const notify = options.notify !== false;

    let rawData = null;
    let resolvedSource = null;

    try {
      // 1. Direct Array or Object passed in
      if (Array.isArray(source)) {
        rawData = source;
        resolvedSource = 'direct-array';
      } else if (source && typeof source === 'object' && !(typeof Blob !== 'undefined' && source instanceof Blob)) {
        rawData = source.boards || source.defaultBoards || source.list || source;
        resolvedSource = 'direct-object';
      } else if (typeof Blob !== 'undefined' && source instanceof Blob) {
        // 2. File / Blob from <input type="file">
        const text = await source.text();
        rawData = JSON.parse(text);
        resolvedSource = source.name || 'file-upload';
      } else if (typeof source === 'string' && (source.trim().startsWith('[') || source.trim().startsWith('{'))) {
        // 3. Raw JSON string
        rawData = JSON.parse(source.trim());
        resolvedSource = 'raw-json-string';
      } else {
        // 4. URL or file path (or auto-discover default candidates if source is null)
        const candidates = [];
        if (source && typeof source === 'string') {
          candidates.push(source);
        } else {
          if (typeof window !== 'undefined' && window.BOARDS_JSON_URL) {
            candidates.push(window.BOARDS_JSON_URL);
          }
          if (typeof window !== 'undefined' && window.DEFAULT_3D_BOARDS_URL) {
            candidates.push(window.DEFAULT_3D_BOARDS_URL);
          }
          if (typeof document !== 'undefined') {
            const meta = document.querySelector('meta[name="boards-json"], meta[name="boards-config"]');
            if (meta?.content) candidates.push(meta.content);
          }
          candidates.push(this.DEFAULT_JSON_PATH);
          candidates.push('boards/boards-config.json');
          candidates.push('./boards/default-boards.json');
          candidates.push('./boards/boards-config.json');
          candidates.push('/boards/default-boards.json');
          candidates.push('/boards/boards-config.json');
        }

        let fetchErr = null;
        for (const candidate of candidates) {
          try {
            if (typeof fetch === 'function') {
              // Append cache-buster timestamp during web sessions to prevent stale caches
              const isHttp = (typeof window !== 'undefined' && window.location?.protocol !== 'file:') ||
                             (typeof candidate === 'string' && (candidate.startsWith('http://') || candidate.startsWith('https://') || candidate.startsWith('/')));
              const fetchUrl = isHttp
                ? `${candidate}${candidate.includes('?') ? '&' : '?'}t=${Date.now()}`
                : candidate;
              const res = await fetch(fetchUrl, { cache: 'no-cache' });
              if (res.ok) {
                rawData = await res.json();
                resolvedSource = candidate;
                break;
              } else {
                fetchErr = new Error(`HTTP ${res.status} ${res.statusText} from ${candidate}`);
              }
            } else if (typeof process !== 'undefined' && process.versions?.node) {
              // Node.js environment fallback using fs
              try {
                const fs = await import('fs/promises');
                const path = await import('path');
                const filePath = path.resolve(process.cwd(), candidate);
                const content = await fs.readFile(filePath, 'utf8');
                rawData = JSON.parse(content);
                resolvedSource = candidate;
                break;
              } catch (nodeErr) {
                fetchErr = nodeErr;
              }
            }
          } catch (e) {
            fetchErr = e;
          }
        }

        // Secondary Node.js fallback if fetch failed or wasn't suitable
        if (!rawData && typeof process !== 'undefined' && process.versions?.node) {
          for (const candidate of candidates) {
            try {
              const fs = await import('fs/promises');
              const path = await import('path');
              const filePath = path.resolve(process.cwd(), candidate);
              const content = await fs.readFile(filePath, 'utf8');
              rawData = JSON.parse(content);
              resolvedSource = candidate;
              break;
            } catch (_) {}
          }
        }

        if (!rawData) {
          throw fetchErr || new Error('Could not resolve or load boards JSON from candidate paths');
        }
      }

      // Handle wrapper object { boards: [...] } or { defaultBoards: [...] }
      let boardsList = rawData;
      if (!Array.isArray(boardsList) && boardsList && typeof boardsList === 'object') {
        boardsList = boardsList.boards || boardsList.defaultBoards || boardsList.list || Object.values(boardsList);
      }

      if (!Array.isArray(boardsList) || boardsList.length === 0) {
        throw new Error('Loaded JSON does not contain a valid non-empty array of board configurations');
      }

      const cleanBoards = [];
      for (const item of boardsList) {
        if (!item || typeof item !== 'object') continue;
        const validRes = this.validateBoardConfig(item);
        if (!validRes.valid) {
          console.warn(`[Board3DCatalog] Skipping invalid board "${item?.id || 'unknown'}":`, validRes.error);
          continue;
        }

        const clean = {
          id: String(item.id).trim(),
          name: String(item.name).trim(),
          brand: item.brand || 'crazy,pints,adult',
          desc: String(item.desc || '').trim(),
          target: Number(item.target) || 121,
          trackCount: Number(item.trackCount) || 3,
          thumbnailUrl: String(item.thumbnailUrl || '').trim(),
          modelUrl: String(item.modelUrl || '').trim(),
          holeSpacing: typeof item.holeSpacing === 'number' ? item.holeSpacing : 7.5,
          cameraDefaults: Object.assign({
            fov: 44,
            position: [0, 48, 38],
            target: [0, 0, 0],
            minDistance: 15,
            maxDistance: 95
          }, item.cameraDefaults || {}),
          lightingPreset: item.lightingPreset || 'studio',
          woodFinish: item.woodFinish || 'maple',
          coordinatesData: item.coordinatesData !== undefined ? item.coordinatesData : (item.holeData || null),
          holeData: item.coordinatesData !== undefined ? item.coordinatesData : (item.holeData || null),
          isCustom: Boolean(item.isCustom)
        };

        cleanBoards.push(clean);
      }

      if (cleanBoards.length === 0) {
        throw new Error('No valid board configurations could be parsed from the JSON source');
      }

      if (replace) {
        this._boards.clear();
      }

      for (const b of cleanBoards) {
        this._boards.set(b.id, b);
      }

      // Mutate and update DEFAULT_3D_BOARDS export
      if (Array.isArray(DEFAULT_3D_BOARDS)) {
        DEFAULT_3D_BOARDS.length = 0;
        DEFAULT_3D_BOARDS.push(...cleanBoards);
      }
      if (typeof window !== 'undefined') {
        window.DEFAULT_3D_BOARDS = DEFAULT_3D_BOARDS;
      }

      this._jsonLoaded = true;
      this._jsonSource = resolvedSource;

      if (syncDropdown) {
        this.syncDropdownOptions();
      }

      if (notify && typeof document !== 'undefined') {
        try {
          document.dispatchEvent(new CustomEvent('board3d:boards-loaded', {
            detail: {
              boards: this.getBoards(),
              defaultBoards: cleanBoards,
              source: resolvedSource,
              count: cleanBoards.length
            }
          }));
        } catch (_) {}
      }

      console.info(`[Board3DCatalog] Successfully loaded ${cleanBoards.length} boards from JSON (${resolvedSource}) without rebuilding!`);

      return {
        success: true,
        count: cleanBoards.length,
        boards: cleanBoards,
        source: resolvedSource
      };
    } catch (err) {
      console.warn('[Board3DCatalog] Failed to load boards from JSON, falling back to embedded defaults:', err?.message || err);
      return {
        success: false,
        error: err?.message || String(err),
        boards: Array.from(this._boards.values()),
        source: null
      };
    }
  }

  /**
   * Convenience alias for loadDefaultBoards.
   * @param {string|Array|Object|File|Blob} [source=null]
   * @param {Object} [options={}]
   * @returns {Promise<{ success: boolean, count: number, boards: Array<Object>, source: string|null, error?: string }>}
   */
  static async loadFromJson(source = null, options = {}) {
    return this.loadDefaultBoards(source, options);
  }

  /**
   * Automatically initiates loading default boards from the JSON config file.
   * Deduplicates concurrent calls.
   * @returns {Promise<{ success: boolean, boards: Array<Object> }>}
   */
  static autoLoad() {
    if (this._jsonLoaded) {
      return Promise.resolve({
        success: true,
        count: this._boards.size,
        boards: Array.from(this._boards.values()),
        source: this._jsonSource
      });
    }
    if (this._jsonLoadPromise) {
      return this._jsonLoadPromise;
    }
    this._jsonLoadPromise = this.loadDefaultBoards().finally(() => {
      this._jsonLoadPromise = null;
    });
    return this._jsonLoadPromise;
  }

  /**
   * Synchronously registers or sets a list of default boards (e.g. from an inlined JSON object).
   * @param {Array<Object>} boardsList
   * @param {Object} [options={}]
   * @returns {{ success: boolean, count: number }}
   */
  static setDefaultBoards(boardsList, options = {}) {
    if (!Array.isArray(boardsList)) {
      return { success: false, error: 'Boards list must be an array' };
    }
    const replace = options.replace !== false;
    if (replace) this._boards.clear();
    for (const item of boardsList) {
      if (item && item.id) {
        this._boards.set(item.id, Object.assign({}, item, { isCustom: false }));
      }
    }
    if (Array.isArray(DEFAULT_3D_BOARDS)) {
      DEFAULT_3D_BOARDS.length = 0;
      DEFAULT_3D_BOARDS.push(...this._boards.values());
    }
    this._jsonLoaded = true;
    this._jsonSource = options.source || 'in-memory';
    if (options.syncDropdown !== false) {
      this.syncDropdownOptions();
    }
    return { success: true, count: this._boards.size };
  }

  /**
   * Restores default boards back to the built-in fallback set.
   */
  static resetDefaultBoards() {
    this._boards.clear();
    for (const b of this._fallbackBoards) {
      this._boards.set(b.id, Object.assign({}, b));
    }
    if (Array.isArray(DEFAULT_3D_BOARDS)) {
      DEFAULT_3D_BOARDS.length = 0;
      DEFAULT_3D_BOARDS.push(...this._boards.values());
    }
    this._jsonLoaded = false;
    this._jsonSource = null;
    this.syncDropdownOptions();
  }

  static getStorage() {
    return (typeof window !== 'undefined' && (window.localStorage || window.appStorage)) ||
           (typeof localStorage !== 'undefined' ? localStorage : null);
  }

  /**
   * Loads custom 3D boards from localStorage/appStorage into memory.
   * @returns {Array<Object>}
   */
  static loadCustomBoards() {
    this._customBoards.clear();
    const s = this.getStorage();
    if (s) {
      try {
        const raw = s.getItem(Board3DCatalog.CUSTOM_STORAGE_KEY);
        if (raw) {
          const list = JSON.parse(raw);
          if (Array.isArray(list)) {
            for (const item of list) {
              if (item && item.id) {
                delete item.coordinatesUrl;
                this._customBoards.set(item.id, Object.assign({}, item, { isCustom: true }));
              }
            }
          }
        }
      } catch (err) {
        console.warn('[Board3DCatalog] Error loading custom boards from storage:', err);
      }
    }

    if (typeof window !== 'undefined' && window.Profile && typeof window.Profile.getInstance === 'function') {
      try {
        const profBoards = window.Profile.getInstance().getCustom3DBoards(s);
        if (profBoards?.list && Array.isArray(profBoards.list)) {
          for (const item of profBoards.list) {
            if (item && item.id && !this._customBoards.has(item.id)) {
              this._customBoards.set(item.id, Object.assign({}, item, { isCustom: true }));
            }
          }
        }
      } catch (_) {}
    }

    this._customLoaded = true;
    return Array.from(this._customBoards.values());
  }

  /**
   * Persists all custom 3D boards to localStorage/appStorage.
   */
  static saveCustomBoards() {
    const s = this.getStorage();
    if (!s) return;
    try {
      const list = Array.from(this._customBoards.values());
      s.setItem(Board3DCatalog.CUSTOM_STORAGE_KEY, JSON.stringify(list));
    } catch (err) {
      console.warn('[Board3DCatalog] Error saving custom boards to storage:', err);
    }
  }

  /**
   * Resets custom boards cache, forcing reload on next access.
   */
  static resetCustomBoards() {
    this._customBoards.clear();
    this._customLoaded = false;
  }

  /**
   * Ensures custom boards have been loaded from storage at least once.
   * @private
   */
  static _ensureCustomLoaded() {
    if (!this._customLoaded) {
      this.loadCustomBoards();
    }
  }

  /**
   * Retrieves all registered 3D boards, optionally filtered by brand.
   * @param {string} [brand='all']
   * @returns {Array<Object>}
   */
  static getBoards(brand = 'all') {
    this._ensureCustomLoaded();
    const all = [
      ...Array.from(this._boards.values()),
      ...Array.from(this._customBoards.values())
    ];
    if (!brand || brand === 'all') return all;
    return all.filter(b => {
      if (!b.brand || b.brand === 'all') return true;
      const brands = String(b.brand).split(',').map(s => s.trim());
      return brands.includes(brand);
    });
  }

  /**
   * Retrieves a specific 3D board definition by ID.
   * @param {string} id
   * @returns {Object|null}
   */
  static getBoardById(id) {
    if (!id) return null;
    this._ensureCustomLoaded();
    return this._customBoards.get(id) || this._boards.get(id) || null;
  }

  /**
   * Retrieves custom 3D boards, optionally filtered by brand.
   * @param {string} [brand='all']
   * @returns {Array<Object>}
   */
  static getCustomBoards(brand = 'all') {
    this._ensureCustomLoaded();
    const list = Array.from(this._customBoards.values());
    if (!brand || brand === 'all') return list;
    return list.filter(b => {
      if (!b.brand || b.brand === 'all') return true;
      const brands = String(b.brand).split(',').map(s => s.trim());
      return brands.includes(brand);
    });
  }

  /**
   * Checks if a board is a custom user-registered board.
   * @param {string} id
   * @returns {boolean}
   */
  static isCustomBoard(id) {
    if (!id) return false;
    this._ensureCustomLoaded();
    return this._customBoards.has(id);
  }

  /**
   * Registers a new 3D cribbage board with model URL and hole coordinate data.
   * @param {Object} boardConfig
   * @returns {{ success: boolean, board?: Object, error?: string }}
   */
  static registerBoard(boardConfig) {
    this._ensureCustomLoaded();
    const validation = this.validateBoardConfig(boardConfig);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    const clean = {
      id: String(boardConfig.id).trim(),
      name: String(boardConfig.name).trim(),
      brand: boardConfig.brand || 'all',
      desc: String(boardConfig.desc || '').trim(),
      target: Number(boardConfig.target) || 121,
      trackCount: Number(boardConfig.trackCount) || 3,
      thumbnailUrl: String(boardConfig.thumbnailUrl || '').trim(),
      modelUrl: String(boardConfig.modelUrl || '').trim(),
      coordinatesData: boardConfig.coordinatesData !== undefined ? boardConfig.coordinatesData : (boardConfig.holeData || null),
      holeData: boardConfig.coordinatesData !== undefined ? boardConfig.coordinatesData : (boardConfig.holeData || null),
      cameraDefaults: Object.assign({
        fov: 44,
        position: [0, 48, 38],
        target: [0, 0, 0],
        minDistance: 15,
        maxDistance: 95
      }, boardConfig.cameraDefaults || {}),
      lightingPreset: boardConfig.lightingPreset || 'studio',
      woodFinish: boardConfig.woodFinish || 'maple',
      isCustom: true
    };

    this._customBoards.set(clean.id, clean);
    this.saveCustomBoards();
    this.syncDropdownOptions();

    // Two-way synchronization with player profile
    try {
      if (typeof window !== 'undefined' && window.Profile && typeof window.Profile.getInstance === 'function') {
        window.Profile.getInstance().saveCustom3DBoard(clean, clean.brand);
      }
    } catch (_) {}

    return { success: true, board: clean };
  }

  /**
   * Alias for registerBoard.
   * @param {Object} boardConfig
   * @returns {{ success: boolean, board?: Object, error?: string }}
   */
  static addCustomBoard(boardConfig) {
    return this.registerBoard(boardConfig);
  }

  /**
   * Updates an existing custom 3D board definition.
   * @param {string} id
   * @param {Object} updates
   * @returns {{ success: boolean, board?: Object, error?: string }}
   */
  static updateCustomBoard(id, updates = {}) {
    this._ensureCustomLoaded();
    if (!id || !this._customBoards.has(id)) {
      return { success: false, error: 'Custom board not found or cannot edit default boards.' };
    }

    const existing = this._customBoards.get(id);

    if (updates.name !== undefined) {
      if (typeof updates.name !== 'string' || updates.name.trim().length === 0) {
        return { success: false, error: 'Board name is required and cannot be empty.' };
      }
    }

    if (updates.holeData !== undefined && updates.holeData !== null) {
      const holeVal = this.validateHoleCoordinates(updates.holeData);
      if (!holeVal.valid) {
        return { success: false, error: holeVal.error || 'Invalid hole coordinate dataset structure.' };
      }
    }

    const updated = {
      ...existing,
      name: updates.name !== undefined ? String(updates.name).trim() : existing.name,
      brand: updates.brand !== undefined ? updates.brand : (existing.brand || 'all'),
      desc: updates.desc !== undefined ? String(updates.desc).trim() : existing.desc,
      thumbnailUrl: updates.thumbnailUrl !== undefined ? String(updates.thumbnailUrl).trim() : (existing.thumbnailUrl || ''),
      modelUrl: updates.modelUrl !== undefined ? String(updates.modelUrl).trim() : existing.modelUrl,
      coordinatesData: updates.coordinatesData !== undefined ? updates.coordinatesData : (updates.holeData !== undefined ? updates.holeData : existing.coordinatesData),
      holeData: updates.coordinatesData !== undefined ? updates.coordinatesData : (updates.holeData !== undefined ? updates.holeData : existing.holeData),
      trackCount: updates.trackCount !== undefined ? Number(updates.trackCount) || existing.trackCount : existing.trackCount,
      target: updates.target !== undefined ? Number(updates.target) || existing.target : existing.target,
      woodFinish: updates.woodFinish !== undefined ? updates.woodFinish : existing.woodFinish,
      lightingPreset: updates.lightingPreset !== undefined ? updates.lightingPreset : existing.lightingPreset,
      cameraDefaults: updates.cameraDefaults !== undefined ? { ...existing.cameraDefaults, ...updates.cameraDefaults } : existing.cameraDefaults,
      isCustom: true
    };
    delete updated.coordinatesUrl;

    this._customBoards.set(id, updated);
    this.saveCustomBoards();
    this.syncDropdownOptions();

    // Two-way synchronization with player profile
    try {
      if (typeof window !== 'undefined' && window.Profile && typeof window.Profile.getInstance === 'function') {
        window.Profile.getInstance().saveCustom3DBoard(updated, updated.brand);
      }
    } catch (_) {}

    return { success: true, board: updated };
  }

  /**
   * Renames an existing custom 3D board.
   * @param {string} id
   * @param {string} newName
   * @returns {{ success: boolean, board?: Object, error?: string }}
   */
  static renameCustomBoard(id, newName) {
    if (!newName || typeof newName !== 'string' || !newName.trim()) {
      return { success: false, error: 'Board name cannot be empty.' };
    }
    return this.updateCustomBoard(id, { name: newName.trim() });
  }

  /**
   * Deletes a custom 3D board by ID.
   * @param {string} id
   * @returns {{ success: boolean, error?: string }}
   */
  static deleteCustomBoard(id) {
    this._ensureCustomLoaded();
    if (!id || !this._customBoards.has(id)) {
      return { success: false, error: 'Cannot delete: custom board not found or is a built-in default board.' };
    }

    this._customBoards.delete(id);
    this.saveCustomBoards();
    this.syncDropdownOptions();

    // Two-way synchronization with player profile
    try {
      if (typeof window !== 'undefined' && window.Profile && typeof window.Profile.getInstance === 'function') {
        window.Profile.getInstance().deleteCustom3DBoard(id);
      }
    } catch (_) {}

    return { success: true };
  }

  /**
   * Synchronizes the DOM #opt3DBoard <select> element with default and custom boards filtered by brand.
   * If only 1 board is available for the current game, that option will automatically be selected.
   * @param {HTMLSelectElement} [selectEl]
   * @param {string} [selectedId]
   * @param {string} [brand]
   */
  static syncDropdownOptions(selectEl = null, selectedId = null, brand = null) {
    if (typeof document === 'undefined') return;
    const targetEls = selectEl
      ? [selectEl]
      : [document.getElementById('opt3DBoard'), document.getElementById('inGameOpt3DBoard')].filter(Boolean);
    if (targetEls.length === 0) return;

    this._ensureCustomLoaded();

    const resolvedBrand = brand || (typeof window !== 'undefined' && typeof window.getAppBrand === 'function'
      ? window.getAppBrand()
      : (typeof window !== 'undefined' && window.currentBrand
        ? window.currentBrand
        : (typeof window !== 'undefined' && typeof window.detectBrand === 'function'
          ? window.detectBrand()
          : 'crazy')));

    const isBrandAllowed = (b) => {
      if (!b.brand || b.brand === 'all') return true;
      const brands = String(b.brand).split(',').map(s => s.trim());
      return brands.includes(resolvedBrand);
    };

    let allowedDefaults = Array.from(this._boards.values()).filter(isBrandAllowed);
    let allowedCustom = Array.from(this._customBoards.values()).filter(isBrandAllowed);

    // Defensive fallback if no boards match brand
    if (allowedDefaults.length === 0 && allowedCustom.length === 0) {
      allowedDefaults = Array.from(this._boards.values());
      allowedCustom = Array.from(this._customBoards.values());
    }

    const allAllowed = [...allowedDefaults, ...allowedCustom];

    let targetId = null;
    if (allAllowed.length === 1) {
      targetId = allAllowed[0].id;
    } else {
      const currentVal = selectedId || (typeof window !== 'undefined' && window.settings?.selected3DBoardId) || (targetEls[0] && targetEls[0].value) || (resolvedBrand === 'clash' ? 'board-cribbage-clash' : 'board-14');
      if (allAllowed.some(b => b.id === currentVal)) {
        targetId = currentVal;
      } else {
        targetId = (allAllowed.find(b => b.id === 'board-14') || allAllowed[0]).id;
      }
    }

    for (const el of targetEls) {
      el.innerHTML = '';

      if (allowedCustom.length === 0) {
        for (const b of allowedDefaults) {
          const opt = document.createElement('option');
          opt.value = b.id;
          opt.textContent = b.name;
          if (b.id === targetId) opt.selected = true;
          el.appendChild(opt);
        }
      } else {
        if (allowedDefaults.length > 0) {
          const defGroup = document.createElement('optgroup');
          defGroup.label = 'Default 3D Boards';
          for (const b of allowedDefaults) {
            const opt = document.createElement('option');
            opt.value = b.id;
            opt.textContent = b.name;
            if (b.id === targetId) opt.selected = true;
            defGroup.appendChild(opt);
          }
          el.appendChild(defGroup);
        }

        const customGroup = document.createElement('optgroup');
        customGroup.label = 'Custom 3D Boards';
        for (const b of allowedCustom) {
          const opt = document.createElement('option');
          opt.value = b.id;
          opt.textContent = `${b.name} (Custom)`;
          if (b.id === targetId) opt.selected = true;
          customGroup.appendChild(opt);
        }
        el.appendChild(customGroup);
      }

      if (targetId) {
        el.value = targetId;
      }
    }

    if (targetId && typeof window !== 'undefined' && window.settings) {
      window.settings.selected3DBoardId = targetId;
    }
  }

  /**
   * Validates a board definition schema.
   * @param {Object} cfg
   * @returns {{ valid: boolean, error?: string }}
   */
  static validateBoardConfig(cfg) {
    if (!cfg || typeof cfg !== 'object') {
      return { valid: false, error: 'Board configuration must be an object' };
    }
    if (!cfg.id || typeof cfg.id !== 'string' || cfg.id.trim().length === 0) {
      return { valid: false, error: 'Board ID is required and must be a non-empty string' };
    }
    if (!cfg.name || typeof cfg.name !== 'string' || cfg.name.trim().length === 0) {
      return { valid: false, error: 'Board name is required' };
    }
    if (cfg.modelUrl && typeof cfg.modelUrl !== 'string') {
      return { valid: false, error: 'modelUrl must be a valid URL string' };
    }
    const coordsData = cfg.coordinatesData !== undefined ? cfg.coordinatesData : cfg.holeData;
    if (coordsData && !this.validateHoleCoordinates(coordsData).valid) {
      return { valid: false, error: 'Invalid hole coordinate dataset structure' };
    }
    return { valid: true };
  }

  /**
   * Validates hole coordinate JSON dataset structure.
   * Accepts `{ tracks: { p1: [ {hole, x, y, z} ] } }`, root `{ p1: [ {hole, x, y, z} ] }`, and flat array format.
   * @param {Object|Array} data
   * @returns {{ valid: boolean, error?: string }}
   */
  static validateHoleCoordinates(data) {
    if (!data || typeof data !== 'object') {
      return { valid: false, error: 'Hole coordinates must be an object or array' };
    }

    let tracks = null;
    if (Array.isArray(data)) {
      tracks = { p1: data };
    } else if (data.tracks && typeof data.tracks === 'object') {
      tracks = data.tracks;
    } else if (typeof data === 'object') {
      // Check if keys are track names with arrays, e.g. { p1: [...], p2: [...] }
      const hasArrays = Object.values(data).some(v => Array.isArray(v));
      if (hasArrays) {
        tracks = data;
      } else {
        return { valid: false, error: 'Coordinate data missing tracks map' };
      }
    } else {
      return { valid: false, error: 'Coordinate data missing tracks map' };
    }

    for (const trackKey of Object.keys(tracks)) {
      const list = tracks[trackKey];
      if (!Array.isArray(list)) {
        return { valid: false, error: `Track ${trackKey} must be an array of hole coordinates` };
      }
      for (let i = 0; i < list.length; i++) {
        const pt = list[i];
        if (!pt || typeof pt !== 'object') {
          return { valid: false, error: `Invalid point at index ${i} in track ${trackKey}` };
        }
        const px = typeof pt.x === 'number' ? pt.x : (typeof pt[' x'] === 'number' ? pt[' x'] : Number(pt.x ?? pt[' x']));
        const py = typeof pt.y === 'number' ? pt.y : (typeof pt[' y'] === 'number' ? pt[' y'] : Number(pt.y ?? pt[' y']));
        const pz = typeof pt.z === 'number' ? pt.z : (typeof pt[' z'] === 'number' ? pt[' z'] : Number(pt.z ?? pt[' z']));
        if (typeof px !== 'number' || isNaN(px) ||
            typeof py !== 'number' || isNaN(py) ||
            typeof pz !== 'number' || isNaN(pz)) {
          return { valid: false, error: `Point at hole ${pt.hole ?? i} must have valid numeric x, y, z` };
        }
      }
    }
    return { valid: true };
  }

  /**
   * Builds a fast (who, hole) -> { x, y, z } lookup map from coordinate data.
   * If a dataset only contains key landmark holes, smoothly interpolates intermediate holes.
   * @param {Object|Array} holeData
   * @returns {Function} (who, hole) => { x, y, z }
   */
  static createHoleLookup(holeData, scale = 1.0) {
    if (!holeData) return null;
    let tracks = {};
    if (Array.isArray(holeData)) {
      tracks = { p1: holeData };
    } else if (holeData.tracks && typeof holeData.tracks === 'object') {
      tracks = holeData.tracks;
    } else if (typeof holeData === 'object') {
      tracks = holeData;
    }

    const map = new Map();
    const sortedHolesPerTrack = new Map();

    for (const who of Object.keys(tracks)) {
      const list = tracks[who];
      if (Array.isArray(list)) {
        const sorted = [];
        for (const pt of list) {
          const h = Number(pt.hole !== undefined ? pt.hole : (pt.h !== undefined ? pt.h : pt.index));
          if (!isNaN(h)) {
            const px = pt.x !== undefined ? pt.x : pt[' x'];
            const py = pt.y !== undefined ? pt.y : pt[' y'];
            const pz = pt.z !== undefined ? pt.z : pt[' z'];
            const entry = {
              hole: h,
              x: Number(px) * scale,
              y: Number(py) * scale,
              z: Number(pz) * scale,
              normal: Array.isArray(pt.normal) ? pt.normal : [0, 1, 0]
            };
            map.set(`${who}_${h}`, entry);
            sorted.push(entry);
          }
        }
        sorted.sort((a, b) => a.hole - b.hole);
        sortedHolesPerTrack.set(who, sorted);
      }
    }

    const lookupFn = (who, holeNum) => {
      let resolvedWho = who;
      if (who === 'you' || who === 'player1' || who === 'team1') resolvedWho = 'p1';
      else if (who === 'cpu' || who === 'player2' || who === 'team2') resolvedWho = 'p2';
      else if (who === 'player3' || who === 'team3') resolvedWho = 'p3';
      else if (who === 'player4' || who === 'team4') resolvedWho = 'p4';

      const key = `${resolvedWho}_${holeNum}`;
      if (map.has(key)) return map.get(key);

      const p1Key = `p1_${holeNum}`;
      if (map.has(p1Key)) return map.get(p1Key);

      // Interpolate if hole is between defined holes in track
      const trackList = sortedHolesPerTrack.get(resolvedWho) || sortedHolesPerTrack.get('p1');
      if (trackList && trackList.length > 0) {
        if (holeNum <= trackList[0].hole) return trackList[0];
        if (holeNum >= trackList[trackList.length - 1].hole) return trackList[trackList.length - 1];

        for (let i = 0; i < trackList.length - 1; i++) {
          const a = trackList[i];
          const b = trackList[i + 1];
          if (holeNum >= a.hole && holeNum <= b.hole) {
            const span = b.hole - a.hole;
            const t = span === 0 ? 0 : (holeNum - a.hole) / span;
            return {
              hole: holeNum,
              x: a.x + (b.x - a.x) * t,
              y: a.y + (b.y - a.y) * t,
              z: a.z + (b.z - a.z) * t,
              normal: a.normal
            };
          }
        }
      }

      return null;
    };

    lookupFn.has = (who, holeNum) => {
      let resolvedWho = who;
      if (who === 'you' || who === 'player1' || who === 'team1') resolvedWho = 'p1';
      else if (who === 'cpu' || who === 'player2' || who === 'team2') resolvedWho = 'p2';
      else if (who === 'player3' || who === 'team3') resolvedWho = 'p3';
      else if (who === 'player4' || who === 'team4') resolvedWho = 'p4';
      return map.has(`${resolvedWho}_${holeNum}`) || map.has(`p1_${holeNum}`);
    };

    return lookupFn;
  }
}
